import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import moment from 'moment-timezone';
import { Store } from '@ngxs/store';
import { DealerState } from 'src/app/shared/state/DealerState';
import { distinctUntilChanged, filter, finalize, map, Observable, switchMap } from 'rxjs';
import { SharedDialogComponent } from 'src/app/shared/components/shared-dialog/shared-dialog.component';
import { NotificationBanner } from 'src/app/shared/models/notification-banner.model';
import { bannerErrorKey, NotificationBannerService } from 'src/app/shared/services/notification-banner.service';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { NotificationBannerEditorComponent } from '../notification-banner-editor/notification-banner-editor.component';

@Component({
  selector: 'app-notification-banner-admin',
  standalone: false,
  templateUrl: './notification-banner-admin.component.html',
  styleUrls: ['./notification-banner-admin.component.scss']
})
export class NotificationBannerAdminComponent implements OnInit {
  public readonly translationService = inject(TranslationService);
  public readonly bannerService = inject(NotificationBannerService);
  private readonly dialog = inject(MatDialog);
  private readonly alertService = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);

  public readonly columns = ['title', 'start', 'end', 'status', 'template', 'actions'];
  public readonly timezoneText = this.translationService.translate('bannerTimezoneText').replace('{timezone}', moment.tz.guess());
  public banners: NotificationBanner[] = [];
  public loading = false;
  public loadFailed = false;
  public busy = false;
  public enabled = true;

  ngOnInit(): void {
    this.store
      .select(DealerState.bannersEnabled)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.enabled = enabled;
        this.banners = [];
        this.loadBanners();
      });
  }

  loadBanners(): void {
    if (!this.enabled) {
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    this.bannerService
      .listBanners()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (banners) => (this.banners = this.enabled ? banners : []),
        error: () => (this.loadFailed = true)
      });
  }

  openEditor(id?: string): void {
    if (!this.enabled) {
      return;
    }
    this.dialog
      .open(NotificationBannerEditorComponent, { data: { id }, width: '800px', maxWidth: '95vw', disableClose: true })
      .afterClosed()
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.showAlert(AlertType.success, 'bannerSaveSuccessText');
        this.loadBanners();
      });
  }

  toggleActive(banner: NotificationBanner): void {
    if (!this.enabled) {
      return;
    }
    if (banner.isActive) {
      this.run(this.bannerService.deactivateBanner(banner.id), 'bannerDeactivateSuccessText', 'bannerDeactivateErrorText');
    } else {
      this.run(this.bannerService.activateBanner(banner.id), 'bannerActivateSuccessText', 'bannerActivateErrorText');
    }
  }

  deleteBanner(banner: NotificationBanner): void {
    if (!this.enabled) {
      return;
    }
    const translate = (key: string) => this.translationService.translate(key);
    const confirmed = this.dialog
      .open<SharedDialogComponent, SharedDialogComponent['data'], boolean>(SharedDialogComponent, {
        data: {
          title: translate('bannerDeleteText'),
          message: translate('bannerDeleteConfirmText').replace('{title}', banner.title),
          buttonText: translate('bannerDeleteText'),
          cancelButtonText: translate('cancelText')
        }
      })
      .afterClosed()
      .pipe(filter((result) => result === true && this.enabled));
    this.run(
      confirmed.pipe(switchMap(() => this.bannerService.deleteBanner(banner.id).pipe(map(() => true)))),
      'bannerDeleteSuccessText',
      'bannerDeleteErrorText'
    );
  }

  statusKey(banner: NotificationBanner): string {
    const now = Date.now();
    if (!banner.isActive) {
      return 'inactiveText';
    }
    if (now < Date.parse(banner.startAt)) {
      return 'bannerScheduledText';
    }
    return now < Date.parse(banner.endAt) ? 'bannerLiveText' : 'bannerExpiredText';
  }

  /** A null result means the admin declined an overlap replacement, so nothing changed. */
  private run(action: Observable<unknown>, successKey: string, errorKey: string): void {
    this.busy = true;
    action
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.busy = false))
      )
      .subscribe({
        next: (result) => {
          if (result !== null) {
            this.showAlert(AlertType.success, successKey);
            this.loadBanners();
          }
        },
        error: (error: unknown) => {
          this.showAlert(AlertType.critical, bannerErrorKey(error, errorKey));
          this.loadBanners();
        }
      });
  }

  private showAlert(type: AlertType, key: string): void {
    this.alertService.showAlert({ type, content: this.translationService.translate(key) } as Alert);
  }
}
